import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useUserContext } from '@/hooks/useUserContext';

import { UserContextData } from './types';

type AuthContextType = {
  authenticationState?: string | undefined;
  callerId?: number | undefined;
  isCSR: boolean;
  customerId?: number | undefined;
  callerLogonId: string | undefined;
  userLogonId: string | undefined;
};

export const AuthContext = createContext<AuthContextType>({
  authenticationState: undefined,
  callerId: undefined,
  isCSR: false,
  customerId: undefined,
  callerLogonId: undefined,
  userLogonId: undefined,
});

export const useAuthContext = () => useContext(AuthContext);

type AuthProviderProps = {
  children: ReactNode;
  userContext?: UserContextData;
  market: string;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  userContext: userContextData,
  market,
}) => {
  const shouldFetchClientSide = !userContextData;
  const { data: clientUserContextData } = useUserContext(market, shouldFetchClientSide);

  const userContext = useMemo(() => {
    if (userContextData) return userContextData;
    if (clientUserContextData) return clientUserContextData;
    return null;
  }, [userContextData, clientUserContextData]);

  const authenticationState = userContext?.authenticationState;
  const isCSR = userContext?.isCSR ?? false;
  const callerId = userContext?.callerId ?? undefined;
  const customerId = userContext?.userId ?? undefined;
  const callerLogonId = userContext?.callerLogonId ?? undefined;
  const userLogonId = userContext?.userLogonId ?? undefined;

  return (
    <AuthContext.Provider
      value={{
        authenticationState,
        callerId,
        isCSR,
        customerId,
        callerLogonId,
        userLogonId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
